// -----------------------------------------------------------------------------
// Floating @-mention picker shown above the chat composer. Two-stage, like the
// omni search palette: it first lists the type "folders" (Users, Vehicles, …)
// and, once one is chosen, the scoped result rows. Purely presentational — the
// active index, stage and keyboard handling live in the composer so the same
// list backs both the new-message and inline-edit editors. Mouse-down is
// swallowed so picking a row never steals focus / collapses the editor's caret.
// -----------------------------------------------------------------------------

import { ChevronLeftIcon } from "@heroicons/react/20/solid";

import classNames from "../../utils/classNames";
import { SearchPaletteItemType } from "../shared/Inputs/SearchPaletteTypes";
import {
  getTypeIcon,
  getTypePillClasses,
} from "../shared/Inputs/SearchPaletteTypePill";

export interface MentionRow {
  key: string;
  paletteType: SearchPaletteItemType;
  label: string;
  description?: string;
  /** When true, show the type label on the trailing edge (result rows). */
  showTypeTag?: boolean;
}

interface MentionPopoverProps {
  open: boolean;
  /** Header label when a type is selected (the chosen folder's name). */
  header?: string;
  rows: MentionRow[];
  activeIndex: number;
  isLoading: boolean;
  emptyHint?: string;
  onHover: (index: number) => void;
  onPick: (index: number) => void;
}

export default function MentionPopover({
  open,
  header,
  rows,
  activeIndex,
  isLoading,
  emptyHint,
  onHover,
  onPick,
}: MentionPopoverProps) {
  if (!open) return null;

  return (
    <div
      className="absolute bottom-full left-0 z-30 mb-1 max-h-64 w-80 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
      // Keep the editor focused + its caret intact when a row is clicked.
      onMouseDown={(e) => e.preventDefault()}
    >
      {header ? (
        <div className="mb-1 flex items-center gap-1 border-b border-gray-100 px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
          <ChevronLeftIcon className="h-3.5 w-3.5" />
          <span>{header}</span>
          <span className="ml-auto normal-case tracking-normal text-gray-300">
            Esc to go back
          </span>
        </div>
      ) : null}

      {rows.map((row, index) => {
        const Icon = getTypeIcon(row.paletteType);
        const isActive = index === activeIndex;

        return (
          <button
            key={row.key}
            type="button"
            onMouseEnter={() => onHover(index)}
            onClick={() => onPick(index)}
            className={classNames(
              "flex w-full items-center gap-2.5 px-3 py-1.5 text-left",
              isActive ? "bg-primary-50" : "hover:bg-gray-50"
            )}
          >
            <span
              className={classNames(
                "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                getTypePillClasses(row.paletteType, true)
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-gray-900">
                {row.label}
              </span>
              {row.description ? (
                <span className="block truncate text-xs text-gray-400">
                  {row.description}
                </span>
              ) : null}
            </span>
            {row.showTypeTag ? (
              <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">
                {row.paletteType}
              </span>
            ) : null}
          </button>
        );
      })}

      {rows.length === 0 && isLoading ? (
        <p className="px-3 py-2 text-xs text-gray-400">Searching…</p>
      ) : null}

      {rows.length === 0 && !isLoading && emptyHint ? (
        <p className="px-3 py-2 text-xs text-gray-400">{emptyHint}</p>
      ) : null}

      {rows.length > 0 && isLoading ? (
        <p className="px-3 py-1.5 text-[11px] text-gray-300">Searching more…</p>
      ) : null}
    </div>
  );
}
